/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import { useReducer, useEffect, useCallback } from 'react';
import { useLogin } from 'react-admin';

import Auth from '../providers/auth-service';

export const STEP_INIT = 'INIT';
export const STEP_LOGOUT = 'LOGOUT';
export const STEP_FETCHING = 'FETCHING';
export const STEP_LOGGED = 'LOGGED';
export const STEP_ERROR = 'ERROR';
export const STEP_ERROR_LOGGED = 'ERROR_LOGGED';

const initialState = {
  step: STEP_INIT,
  fetching: false,
  error: null,
};

function reducer(state, { type, payload }) {
  switch (type) {
    case 'logout':
      return { ...state, step: STEP_LOGOUT, fetching: false };
    case 'fetching':
      return { ...state, step: STEP_FETCHING, fetching: true };
    case 'logged':
      return {
        ...state,
        step: STEP_LOGGED,
        fetching: false,
      };
    case 'error_logged':
      return {
        ...state,
        step: STEP_ERROR_LOGGED,
        error: payload.error,
        fetching: false,
      };
    case 'error':
      return {
        ...state,
        step: STEP_ERROR,
        error: payload.error,
        fetching: false,
      };
    default:
      return state;
  }
}

export default function useIMS() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const login = useLogin();

  useEffect(() => {
    async function testAuth() {
      dispatch({ type: 'fetching' });

      try {
        if (await Auth.isAuthenticated()) {
          dispatch({ type: 'logged' });
          await Auth.restoreSession();
          if (Auth.isUser() || Auth.isAdmin()) login();
          else
            dispatch({
              type: 'error_logged',
              payload: { error: { reason: 'notauthorized' } },
            });
        } else {
          dispatch({ type: 'logout' });
        }
      } catch (error) {
        dispatch({
          type: 'error',
          payload: { error },
        });
      }
    }

    testAuth();

    //eslint-disable-next-line
  }, []);

  const handleLogin = useCallback(() => {
    dispatch({ type: 'fetching' });
    Auth.login();
  }, []);

  const handleLogout = useCallback(() => {
    Auth.logout();
    dispatch({ type: 'logout' });
  }, []);

  return {
    step: state.step,
    error: state.error,
    login: handleLogin,
    logout: handleLogout,
  };
}
